import { useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import toast from 'react-hot-toast'
import { GridColDef } from '@mui/x-data-grid'
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Typography } from '@mui/material'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import translate from '../../i18n/translate'
import { StaticOtpItem, userApi } from '../../modules/services/userService'
import OtpPhonesPage, { formatOtpDate, OtpUserCell, useOtpListState } from '../UI/OtpPhonesPage'
import StaticOtpModal from './StaticOtpModal'

const StaticOtpScreen = () => {
  const intl = useIntl()
  const listState = useOtpListState()

  const [itemToRemove, setItemToRemove] = useState<StaticOtpItem | null>(null)
  // null = closed, { item: null } = add, { item } = edit
  const [editor, setEditor] = useState<{ item: StaticOtpItem | null } | null>(null)

  const { data, isFetching } = userApi.useGetStaticOtpListQuery(listState.query)
  const [removeStaticOtp, { isLoading: isRemoving }] = userApi.useRemoveStaticOtpMutation()

  const onCopyCode = (code: string) => {
    navigator.clipboard.writeText(code)
    toast.success(intl.formatMessage({ id: 'staticOtpCodeCopied' }))
  }

  const onRemoveConfirm = async () => {
    if (!itemToRemove) return

    try {
      await removeStaticOtp(itemToRemove.id).unwrap()
      toast.success(intl.formatMessage({ id: 'staticOtpRemoveSuccess' }))
    } catch {
      toast.error(intl.formatMessage({ id: 'staticOtpRemoveFailed' }))
    } finally {
      setItemToRemove(null)
    }
  }

  const columns = useMemo<GridColDef<StaticOtpItem>[]>(() => [
    {
      field: 'phone',
      flex: 1,
      sortable: false,
      headerName: intl.formatMessage({ id: 'otpListPhone' }),
    },
    {
      field: 'user',
      flex: 1,
      sortable: false,
      headerName: intl.formatMessage({ id: 'otpListUser' }),
      renderCell: ({ row }) => <OtpUserCell user={row.user} />,
    },
    {
      field: 'code',
      flex: 1,
      sortable: false,
      headerName: intl.formatMessage({ id: 'staticOtpCode' }),
      renderCell: ({ row }) => (
        <Box display="flex" alignItems="center" gap={1}>
          <Typography sx={{ fontFamily: 'monospace', fontSize: '18px', letterSpacing: '2px' }}>{row.code}</Typography>
          <IconButton size="small" onClick={() => onCopyCode(row.code)}>
            <ContentCopyIcon fontSize="small" />
          </IconButton>
        </Box>
      ),
    },
    {
      field: 'adminUpdatedAt',
      flex: 1,
      sortable: false,
      headerName: intl.formatMessage({ id: 'staticOtpSetAt' }),
      renderCell: ({ row }) => formatOtpDate(row.adminUpdatedAt),
    },
    {
      field: 'lastLoginDate',
      flex: 1,
      sortable: false,
      headerName: intl.formatMessage({ id: 'staticOtpLastLogin' }),
      renderCell: ({ row }) => formatOtpDate(row.lastLoginDate),
    },
    {
      field: 'actions',
      flex: 1,
      sortable: false,
      headerName: intl.formatMessage({ id: 'otpListActions' }),
      renderCell: ({ row }) => (
        <Box display="flex" flexDirection="column" gap={1}>
          <Button color="warning" onClick={() => setEditor({ item: row })}>
            {translate('staticOtpEditButton')}
          </Button>
          <Button color="warning" onClick={() => setItemToRemove(row)}>
            {translate('staticOtpRemove')}
          </Button>
        </Box>
      ),
    },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [intl])

  return (
    <OtpPhonesPage
      title={translate('staticOtpScreenTitle')}
      addLabel={translate('staticOtpAdd')}
      onAdd={() => setEditor({ item: null })}
      listState={listState}
      rows={data?.list || []}
      count={data?.count || 0}
      columns={columns}
      loading={isFetching}
    >
      {editor && <StaticOtpModal item={editor.item} onClose={() => setEditor(null)} />}

      <Dialog open={!!itemToRemove} onClose={() => setItemToRemove(null)}>
        <DialogTitle>{translate('staticOtpRemove')}</DialogTitle>
        <DialogContent>
          <Typography>{intl.formatMessage({ id: 'staticOtpRemoveConfirm' }, { phone: itemToRemove?.phone })}</Typography>
        </DialogContent>
        <DialogActions>
          <Button color="inherit" onClick={() => setItemToRemove(null)}>
            {translate('cancel')}
          </Button>
          <Button color="warning" variant="contained" onClick={onRemoveConfirm} disabled={isRemoving}>
            {translate('staticOtpRemove')}
          </Button>
        </DialogActions>
      </Dialog>
    </OtpPhonesPage>
  )
}

export default StaticOtpScreen
